import AttachmentUploadCard from 'components/ui/AttachmentUploadCard';
import { XIcon } from 'components/ui/icons';
import { convertToBase64 } from 'lib/PublicFunctions';
import React from 'react';
import Dropzone from 'react-dropzone';
import styled from '@emotion/styled';
import { UploadButton } from './styles';

interface PropTypes {
  attachments: AttachmentLike[];
  setAttachments: (attachments: AttachmentLike[]) => void;
  disabled: boolean;
  title?: string;
  badgeLabel?: string;
  helperText?: string;
  dropzoneText?: string;
  dropzoneActiveText?: string;
  accentColor?: string;
  badgeBackground?: string;
  maxWidth?: number | string;
  containerStyle?: React.CSSProperties;
  variant?: 'card' | 'basic';
}

interface AttachmentLike {
  file: string;
  actualFileName: string;
}

const getAttachmentKey = (attachment: AttachmentLike) => `${attachment.actualFileName}\u0000${attachment.file}`;

const dedupeAttachments = (attachments: AttachmentLike[]) => {
  const seen = new Set<string>();

  return attachments.filter(attachment => {
    const key = getAttachmentKey(attachment);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const AttachmentsSection = styled.div<{ disabled: boolean }>`
  width: 100%;
  opacity: ${props => (props.disabled ? 0.6 : 1)};
`;

const AttachmentsRow = styled.div<{ disabled: boolean; dragActive: boolean }>`
  align-items: center;
  border: 1px solid ${props => (props.dragActive ? '#333333' : '#e5e5ea')};
  border-radius: 4px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 8px;
  padding: 12px 16px;
  width: 100%;
  background: ${props => (props.disabled ? '#f7f7f9' : '#ffffff')};
`;

const AttachmentTag = styled.div`
  align-items: center;
  background-color: #f7f7f9;
  border-radius: 5px;
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  min-height: 40px;
  padding: 10px 16px;
`;

const AttachmentName = styled.span`
  color: #333333;
  font-size: 14px;
  margin-right: 12px;
  overflow-wrap: anywhere;
`;

const RemoveButton = styled.button<{ disabled: boolean }>`
  background: transparent;
  border: 0;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  flex-shrink: 0;
  padding: 0;
`;

const AttachmentActionButton = styled(UploadButton)`
  margin-right: 0;

  &:disabled {
    background-color: #9b9b9b;
    cursor: default;
  }
`;

const VendorEmailAttachments = (props: PropTypes) => {
  const onDrop = async (newFiles: File[]) => {
    if (props.disabled || newFiles.length === 0) {
      return;
    }

    const uploadedFiles = await Promise.all(
      newFiles.map(async file => ({
        file: (await convertToBase64(file)) as string,
        actualFileName: file.name,
      }))
    );

    props.setAttachments(dedupeAttachments([...props.attachments, ...uploadedFiles]));
  };

  const removeAttachment = (index: number) => {
    if (props.disabled) {
      return;
    }

    props.setAttachments(props.attachments.filter((_, currentIndex) => currentIndex !== index));
  };

  if (props.variant === 'basic') {
    return (
      <AttachmentsSection
        disabled={props.disabled}
        style={{
          ...props.containerStyle,
        }}
      >
        <Dropzone onDrop={onDrop} noClick disabled={props.disabled}>
          {({ getRootProps, getInputProps, isDragActive, open }) => (
            <>
              <div {...getRootProps()}>
                <AttachmentsRow disabled={props.disabled} dragActive={isDragActive}>
                  <input {...getInputProps()} />
                  <AttachmentName>
                    {isDragActive
                      ? 'Drop files here to attach them to the vendor email'
                      : 'Attach additional files to the vendor email'}
                  </AttachmentName>
                  <AttachmentActionButton
                    type="button"
                    disabled={props.disabled}
                    onClick={event => {
                      event.stopPropagation();
                      if (!props.disabled) {
                        open();
                      }
                    }}
                  >
                    Add Files
                  </AttachmentActionButton>
                </AttachmentsRow>
              </div>
              {props.attachments.map((attachment, index) => (
                <AttachmentTag key={`${attachment.actualFileName}-${index}`}>
                  <AttachmentName>{attachment.actualFileName}</AttachmentName>
                  <RemoveButton
                    type="button"
                    disabled={props.disabled}
                    onClick={() => removeAttachment(index)}
                  >
                    <XIcon />
                  </RemoveButton>
                </AttachmentTag>
              ))}
            </>
          )}
        </Dropzone>
      </AttachmentsSection>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        opacity: props.disabled ? 0.6 : 1,
        ...props.containerStyle,
      }}
    >
      <AttachmentUploadCard
        title={props.title ?? 'Vendor Email Attachments'}
        badgeLabel={props.badgeLabel ?? 'Vendor receives'}
        helperText={props.helperText ?? ''}
        dropzoneText={props.dropzoneText ?? 'Add files for the vendor to receive'}
        dropzoneActiveText={
          props.dropzoneActiveText ?? 'Drop files here to send them to the vendor'
        }
        files={props.attachments}
        onFilesSelected={onDrop}
        onRemoveFile={removeAttachment}
        disabled={props.disabled}
        accentColor={props.accentColor ?? '#2f86d1'}
        badgeBackground={props.badgeBackground ?? '#eaf4fd'}
        maxWidth={props.maxWidth ?? 480}
      />
    </div>
  );
};

export default VendorEmailAttachments;
